import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Sparkles } from "lucide-react";
import { getSuggestions } from "./assistantApi";
import styles from "./Assistant.module.css";

const VISIBLE = 4;
const ROTATE_MS = 60000;

/**
 * Four clickable suggested questions, rotating every minute.
 *
 * The backend returns i18n keys plus params (built from the user's own farms
 * and fields), so the rendered text is correct in all five locales without an
 * LLM call. Clicking sends immediately.
 */
export default function SuggestionCards({ onPick, disabled }) {
  const { t } = useTranslation();
  const [pool, setPool] = useState([]);
  const [offset, setOffset] = useState(0);
  const [paused, setPaused] = useState(false);
  const timer = useRef(null);

  useEffect(() => {
    let cancelled = false;
    getSuggestions()
      .then((list) => { if (!cancelled) setPool(list); })
      .catch(() => { if (!cancelled) setPool([]); });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (paused || pool.length <= VISIBLE) return undefined;
    timer.current = setInterval(
      () => setOffset((o) => (o + VISIBLE) % pool.length),
      ROTATE_MS
    );
    return () => clearInterval(timer.current);
  }, [paused, pool.length]);

  // Wrap around so the last page stays full instead of showing one card.
  const visible = useMemo(() => {
    if (!pool.length) return [];
    return Array.from({ length: Math.min(VISIBLE, pool.length) }, (_, i) => pool[(offset + i) % pool.length]);
  }, [pool, offset]);

  if (!visible.length) return null;

  return (
    <div
      className={styles.suggestions}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div className={styles.suggestionsLabel}>
        <Sparkles size={14} />
        <span>{t("assistant_suggestions_label")}</span>
      </div>
      <div className={styles.suggestionGrid}>
        {visible.map((s, i) => {
          const text = t(s.key, s.params || {});
          return (
            <button
              // Keyed on the offset so each rotation remounts the card and
              // replays the entrance animation.
              key={`${s.id}-${offset}`}
              type="button"
              className={styles.suggestionCard}
              style={{ animationDelay: `${i * 70}ms` }}
              disabled={disabled}
              onClick={() => onPick(text)}
              title={text}
            >
              {text}
            </button>
          );
        })}
      </div>
    </div>
  );
}
